/**
 * useSirCredits — load the SIR (Self-Insured Retention) credit tiers
 * once per session.
 *
 * Same shape as `useRates`: a module-level
 * singleton cache, mirrored state machine
 * (`loading` → `ready | error`). Values come from `GET /v2/sir-credits`
 * (mocked today, see mock-handlers.ts).
 *
 * The tiers are read-only and refreshed at most when Underwriting
 * republishes the table, so caching once per session is the right
 * shape.
 */
import { useEffect, useState } from "react";
import { getSirCredits } from "../api";
import type { SirCreditTier } from "../domain/pricing";

export type SirCreditsQueryState =
    | { status: "loading" }
    | { status: "error"; error: Error }
    | { status: "ready"; data: SirCreditTier[] };

let inflight: Promise<SirCreditTier[]> | null = null;

function loadSirCreditsOnce(): Promise<SirCreditTier[]> {
    if (inflight) return inflight;
    inflight = getSirCredits()
        .then((res) => res.tiers)
        .catch((error) => {
            inflight = null;
            throw error;
        });
    return inflight;
}

export function useSirCredits(): SirCreditsQueryState {
    const [state, setState] = useState<SirCreditsQueryState>({ status: "loading" });

    useEffect(() => {
        let cancelled = false;
        loadSirCreditsOnce()
            .then((data) => {
                if (cancelled) return;
                setState({ status: "ready", data });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setState({
                    status: "error",
                    error: error instanceof Error ? error : new Error("Failed to load SIR credits"),
                });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return state;
}
